import React, { memo, useCallback, useMemo, useState } from 'react';
import Split from 'react-split';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { DOCUMENT_PANE_MIN_WIDTH, MAX_DOCUMENT_PANES } from 'constants/loanApplication';
import { getSplitScreenStyles, splitScreenStyle } from 'styles/splitScreen.styles';
import { getInnerPaneSizes } from 'utils/documentPanes';

import LoanDocumentPane from '../LoanDocumentPane';

import { paneItemStyles } from './LoanDocumentPaneGroup.styles';

/**
 * The second track of the editor split: the open document panes (1 or 2). With two panes they
 * render as the tracks of their OWN react-split so the Doc A | Doc B divider is draggable, while
 * the editor stays in the outer split's first track (never remounted on pane open/close). `key` on
 * the inner split is required because react-split 2.0.14 cannot add a gutter without a remount —
 * that remount is scoped to the light document panes, never the editor.
 */
const LoanDocumentPaneGroup = ({ panes, renderDocuments }) => {
  const theme = useTheme();
  const classes = useMemo(() => getSplitScreenStyles(theme), [theme]);

  const [innerSizes, setInnerSizes] = useState(() => getInnerPaneSizes(MAX_DOCUMENT_PANES));

  const onDragEnd = useCallback(sizes => setInnerSizes(sizes), []);

  const sizes = panes.length === MAX_DOCUMENT_PANES ? innerSizes : getInnerPaneSizes(panes.length);

  return (
    <Box sx={classes.root}>
      <Split
        key={panes.length}
        minSize={DOCUMENT_PANE_MIN_WIDTH}
        sizes={sizes}
        style={splitScreenStyle}
        onDragEnd={onDragEnd}
      >
        {panes.map(pane => (
          <Box key={pane.paneId} sx={paneItemStyles}>
            <LoanDocumentPane pane={pane} renderDocuments={renderDocuments} />
          </Box>
        ))}
      </Split>
    </Box>
  );
};

LoanDocumentPaneGroup.propTypes = {
  panes: PropTypes.arrayOf(PropTypes.shape({ paneId: PropTypes.string })).isRequired,
  renderDocuments: PropTypes.func.isRequired,
};

export default memo(LoanDocumentPaneGroup);
